import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
    UserCircleIcon, 
    BellIcon, 
    ChevronDownIcon, 
    ArrowLeftOnRectangleIcon,
    UserIcon,
    AcademicCapIcon,
    SunIcon,
    MoonIcon,
    VideoCameraIcon,
    ChatBubbleLeftEllipsisIcon
} from '@heroicons/react/24/outline';
import { motion, AnimatePresence } from 'framer-motion';
import { signOut } from 'firebase/auth'; 
import { collection, query, where, onSnapshot, orderBy, limit, getDocs, writeBatch } from 'firebase/firestore'; 
import { auth, db } from '../firebase'; 

const Navbar = ({ user }) => {
    const navigate = useNavigate();
    const [isProfileOpen, setIsProfileOpen] = useState(false);
    const [showNotifications, setShowNotifications] = useState(false);
    
    // ✅ الحالات الخاصة بالجرس
    const [notifications, setNotifications] = useState([]);
    const [hasUnread, setHasUnread] = useState(false);

    // 🌓 نظام التبديل بين الوضع الليلي والنهاري
    const [darkMode, setDarkMode] = useState(() => {
        return localStorage.getItem('theme') === 'dark';
    });

    useEffect(() => {
        if (darkMode) {
            document.documentElement.setAttribute('data-theme', 'dark');
            localStorage.setItem('theme', 'dark');
        } else {
            document.documentElement.setAttribute('data-theme', 'light');
            localStorage.setItem('theme', 'light');
        }
    }, [darkMode]);

  // 🔔 رادار التنبيهات الباروني المحدث (النسخة النهائية والآمنة)
// 🔔 [THE_BARON_ULTIMATE_NAVBAR_LISTENER] - ضع هذا في Navbar.js
useEffect(() => {
    if (!user || !user.uid) return;

    // مراقبة شاملة لكل الإشعارات
    const q = query(collection(db, "notifications"), orderBy("createdAt", "desc"), limit(10));

    // 🔔 [THE_BARON_ULTIMATE_LISTENER_V2] - عدل الـ Listener بهذي الطريقة
const unsubscribe = onSnapshot(q, (snapshot) => {
    const notes = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));

    const myNotes = notes.filter(n => {
        // 1. هل الإشعار موجه لي؟ (عام للكل OR خاص بي)
        const isMine = n.targetId === 'all' || n.targetId === user.uid;
        
        // 2. هنا "السر": لا تمنع أنواع ثانية، بل اسمح بكل الأنواع (محاضرات، اختبارات، رسائل)
        // شيل شرط n.type !== 'chat' إذا كنت تريد الرسائل تطلع بالجرس أيضاً!
        return isMine; 
    });

    setNotifications(myNotes);
    
    // 3. هز الجرس إذا اكو أي شيء جديد "غير مقروء"
    const hasUnread = myNotes.some(n => n.isRead === false);
    setHasUnread(hasUnread);
});

    return () => unsubscribe();
}, [user]);
    // ✅ ميزة التنظيف الذكي
    const clearNotifications = async () => {
    // 1. مسح من الشاشة فوراً
    setNotifications([]);
    setHasUnread(false);

    try {
        // 2. جلب فقط الإشعارات التي تخص المستخدم الحالي (المدرس أو الطالب)
        // المدرس يمسح إشعارات الـ teacher، والطالب يمسح إشعارات الـ targetId الخاص به
        const q = user.role === 'teacher' 
            ? query(collection(db, "notifications"), where("role", "==", "teacher"))
            : query(collection(db, "notifications"), where("targetId", "==", user.uid));

        const snap = await getDocs(q);
        
        // 3. حذف جماعي للنتائج فقط
        const batch = writeBatch(db);
        snap.forEach((d) => batch.delete(d.ref));
        await batch.commit();
        
        console.log("تم تنظيف جرسك بنجاح! 🛡️");
    } catch (error) {
        console.error("خطأ في التنظيف:", error);
    }
};

    const handleBellClick = () => {
        setShowNotifications(!showNotifications);
        setIsProfileOpen(false);
        if (!showNotifications && hasUnread) {
            setHasUnread(false); 
        }
    };

    const handleLogout = async () => {
        try {
            await signOut(auth);
            localStorage.clear();
            sessionStorage.clear();
            navigate('/login', { replace: true });
        } catch (error) {
            console.error("Logout Error:", error);
        }
    };

    return (
        /* ✅ التعديل الرئيسي: z-[1000] و isolate و allow-overflow لضمان السيادة على السايدبار */
        <nav className="sticky top-0 z-[1000] w-full h-16 ios-glass border-b border-white/20 px-4 md:px-6 flex justify-between items-center shadow-sm isolate allow-overflow transition-colors duration-500 dark:bg-black/60" dir="rtl">
            
            {/* 🍏 اللوغو */}
            <div className="flex items-center gap-3 cursor-pointer group" onClick={() => navigate('/')}>
                <div className="w-8 h-8 md:w-10 md:h-10 bg-[#007AFF] rounded-xl flex items-center justify-center shadow-lg shadow-blue-200 transition-transform group-hover:scale-110">
                    <span className="text-white font-black text-lg md:text-xl">N</span>
                </div>
                <h1 className="text-lg md:text-xl font-black tracking-tight hidden xs:block text-gray-900 dark:text-white">نور أكاديمي</h1>
            </div>

            <div className="flex items-center gap-2 md:gap-4 allow-overflow">
                {/* 🌓 زر التبديل */}
                <button onClick={() => setDarkMode(!darkMode)} className="p-2 md:p-2.5 rounded-xl transition-all hover:scale-110 active:scale-90 bg-white/40 dark:bg-white/10 shadow-sm border border-white/20 text-gray-700 dark:text-white">
                    {darkMode ? <SunIcon className="w-5 h-5 md:w-6 md:h-6 text-yellow-400" /> : <MoonIcon className="w-5 h-5 md:w-6 md:h-6 text-blue-600" />}
                </button>

                {/* 🔔 الجرس */}
                <div className="relative allow-overflow">
                    <button onClick={handleBellClick} className={`p-2 md:p-2.5 rounded-xl transition-all relative ${showNotifications ? 'bg-[#007AFF] text-white shadow-lg shadow-blue-200' : 'hover:bg-gray-100/50 dark:hover:bg-white/10 text-gray-500 dark:text-gray-400'}`}>
                        <BellIcon className={`w-5 h-5 md:w-6 md:h-6 ${hasUnread && !showNotifications ? 'animate-[swing_1s_ease-in-out_infinite]' : ''}`} />
                        
                        {hasUnread && (
                            <span className="absolute top-1 right-1 flex h-3 w-3">
                                <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-red-400 opacity-75"></span>
                                <span className="relative inline-flex rounded-full h-2 w-2 bg-red-500 border border-white"></span>
                            </span>
                        )}
                    </button>

                    <AnimatePresence>
                        {showNotifications && (
                            <motion.div 
                                initial={{ opacity: 0, y: 15 }} 
                                animate={{ opacity: 1, y: 0 }} 
                                exit={{ opacity: 0, y: 10 }}
                                className="absolute left-[-20px] md:left-0 mt-4 w-[280px] xs:w-80 bg-white/95 dark:bg-[#1C1C1E]/95 backdrop-blur-2xl shadow-[0_20px_50px_rgba(0,0,0,0.3)] rounded-[32px] border border-white/40 overflow-hidden z-[9999] origin-top force-top"
                            >
                                <div className="p-4 border-b border-white/20 bg-white/30 dark:bg-white/5 flex justify-between items-center gap-2">
                                    <h3 className="font-black text-[10px] uppercase tracking-widest shrink-0 text-gray-900 dark:text-white">مركز التنبيهات</h3>
                                    <button 
                                        onClick={(e) => { e.stopPropagation(); clearNotifications(); }} 
                                        className="text-[10px] font-black text-[#007AFF] dark:text-[#0A84FF] hover:opacity-70 bg-blue-50/50 dark:bg-blue-500/10 px-2 py-1 rounded-lg transition-all active:scale-95"
                                    >
                                        تنظيف الكل
                                    </button>
                                </div>

                                <div className="max-h-80 overflow-y-auto custom-scroll p-2 space-y-2">
                                    {notifications.length > 0 ? notifications.map(note => (
                                        <div key={note.id} className="p-3 rounded-2xl hover:bg-black/5 dark:hover:bg-white/5 transition-all cursor-pointer flex gap-3 group items-center">
                                            <div className="w-9 h-9 rounded-xl bg-blue-50 dark:bg-blue-500/10 text-[#007AFF] dark:text-[#0A84FF] flex items-center justify-center shrink-0 shadow-sm group-hover:scale-110 transition-transform">
                                                {note.type === 'video' ? <VideoCameraIcon className="w-5 h-5"/> : note.type === 'chat' ? <ChatBubbleLeftEllipsisIcon className="w-5 h-5"/> : <AcademicCapIcon className="w-5 h-5"/>}
                                            </div>
                                            <div className="text-right flex-1 overflow-hidden">
                                                <h4 className="text-[11px] font-black leading-tight truncate text-gray-900 dark:text-gray-100">{note.text || note.message}</h4>
                                                <p className="text-[9px] text-gray-400 dark:text-gray-500 mt-1 font-bold">الآن • نظام النور</p>
                                            </div>
                                        </div>
                                    )) : (
                                        <div className="py-12 text-center flex flex-col items-center opacity-40 text-gray-900 dark:text-white">
                                            <BellIcon className="w-8 h-8 mb-2" />
                                            <p className="text-xs font-bold">لا توجد تنبيهات حالياً</p>
                                        </div>
                                    )}
                                </div>
                            </motion.div>
                        )}
                    </AnimatePresence>
                </div>

                {/* 👤 البروفايل */}
                <div className="relative allow-overflow">
                    <div className={`flex items-center gap-2 md:gap-3 p-1 pr-1 md:pr-4 rounded-full border transition-all cursor-pointer shadow-sm ${isProfileOpen ? 'bg-white dark:bg-[#1C1C1E] border-[#007AFF]/30' : 'bg-white/30 dark:bg-white/5 border-white/40 hover:bg-white/50'}`} onClick={() => { setIsProfileOpen(!isProfileOpen); setShowNotifications(false); }}>
                        <div className="text-right hidden lg:block">
                            <p className="text-[11px] font-black leading-none mb-1 text-gray-900 dark:text-white">{user?.name?.split(' ')[0] || "المستخدم"}</p>
                            <p className="text-[9px] text-gray-400 dark:text-gray-500 uppercase tracking-widest font-bold">{user?.role === 'teacher' ? 'أستاذ' : 'طالب'}</p>
                        </div>
                        <div className="w-8 h-8 bg-blue-50 dark:bg-blue-500/20 rounded-full flex items-center justify-center border border-blue-100 dark:border-blue-500/30 overflow-hidden shadow-inner ring-2 ring-transparent hover:ring-[#007AFF]/20 transition-all">
                             {user?.image ? <img src={user.image} className="w-full h-full object-cover" alt="avatar" /> : <UserIcon className="w-4 h-4 md:w-5 md:h-5 text-[#007AFF] dark:text-[#0A84FF]" />}
                        </div>
                        <ChevronDownIcon className={`w-3 h-3 text-gray-400 transition-transform duration-300 hidden md:block ${isProfileOpen ? 'rotate-180' : ''}`} />
                    </div>

                    <AnimatePresence>
                        {isProfileOpen && (
                            <motion.div 
                                initial={{ opacity: 0, scale: 0.95 }} 
                                animate={{ opacity: 1, scale: 1 }} 
                                exit={{ opacity: 0, scale: 0.95 }} 
                                className="absolute top-[110%] left-0 w-52 md:w-60 bg-white/95 dark:bg-[#1C1C1E]/95 backdrop-blur-2xl border border-white/40 rounded-[28px] shadow-[0_20px_50px_rgba(0,0,0,0.3)] p-2 z-[9999] origin-top-left force-top"
                            >
                                <div className="lg:hidden px-3 py-2 border-b border-white/10 dark:border-white/5 mb-1 text-right">
                                    <p className="text-[11px] font-black truncate text-gray-900 dark:text-white">{user?.name || "المستخدم"}</p>
                                    <p className="text-[9px] text-[#007AFF] dark:text-[#0A84FF] font-black uppercase">{user?.role === 'teacher' ? 'أستاذ' : 'طالب'}</p>
                                </div>

                                <button onClick={() => { navigate(user.role === 'teacher' ? '/admin/profile' : '/student/profile'); setIsProfileOpen(false); }} className="w-full text-right p-2.5 md:p-3 hover:bg-[#007AFF] hover:text-white rounded-lg md:rounded-xl text-xs font-black transition-all flex items-center justify-between group text-gray-800 dark:text-gray-200">
                                    <span>الملف الشخصي</span>
                                    <UserCircleIcon className="w-4 h-4 opacity-50" />
                                </button>
                                <div className="h-[1px] bg-black/5 dark:bg-white/5 my-1"></div>
                                <button onClick={handleLogout} className="w-full text-right p-2.5 md:p-3 hover:bg-red-50 dark:hover:bg-red-500/10 text-red-500 rounded-lg md:rounded-xl text-xs font-black transition-all flex items-center justify-between">
                                    <span>خروج</span>
                                    <ArrowLeftOnRectangleIcon className="w-4 h-4" />
                                </button>
                            </motion.div>
                        )}
                    </AnimatePresence>
                </div>
            </div>
        </nav>
    );
};

export default Navbar;